import { PDFArray, PDFDict, PDFDocument, PDFName, PDFObject, PDFRawStream, PDFRef, PDFStream } from "pdf-lib";

type FingerprintState = {
  document: PDFDocument;
  cache: Map<PDFObject, string>;
  active: Set<PDFObject>;
};

/** Hashes bytes without turning large image streams into JavaScript strings. */
async function hashBytes(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", Uint8Array.from(bytes).buffer);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

/** Compares resource content independently of object numbers, including masks and color profiles. */
async function fingerprint(object: PDFObject, state: FingerprintState): Promise<string | undefined> {
  if (object instanceof PDFRef) {
    const resolved = state.document.context.lookup(object);
    return resolved ? fingerprint(resolved, state) : undefined;
  }
  const cached = state.cache.get(object);
  if (cached) return cached;
  // Unusual cyclic resources are left untouched rather than guessed equivalent.
  if (state.active.has(object)) return undefined;
  state.active.add(object);
  try {
    let value: string;
    if (object instanceof PDFRawStream) {
      const dictionary = await fingerprint(object.dict, state);
      if (!dictionary) return undefined;
      value = `stream:${dictionary}:${await hashBytes(object.getContents())}`;
    } else if (object instanceof PDFStream) {
      return undefined;
    } else if (object instanceof PDFDict) {
      const entries = object.entries().sort(([left], [right]) => left.toString().localeCompare(right.toString()));
      const parts: string[] = [];
      for (const [key, entry] of entries) {
        const child = await fingerprint(entry, state);
        if (!child) return undefined;
        parts.push(`${key.toString()}:${child}`);
      }
      value = `dict:${parts.join(";")}`;
    } else if (object instanceof PDFArray) {
      const parts: string[] = [];
      for (const entry of object.asArray()) {
        const child = await fingerprint(entry, state);
        if (!child) return undefined;
        parts.push(child);
      }
      value = `array:${parts.join(";")}`;
    } else {
      value = object.toString();
    }
    const result = await hashBytes(new TextEncoder().encode(value));
    state.cache.set(object, result);
    return result;
  } finally {
    state.active.delete(object);
  }
}

/** Redirects resource references while keeping each page's resource names and drawing commands intact. */
function replaceReferences(object: PDFObject, replacements: Map<string, PDFRef>, visited: Set<PDFObject>): void {
  if (visited.has(object)) return;
  visited.add(object);
  if (object instanceof PDFStream) {
    replaceReferences(object.dict, replacements, visited);
  } else if (object instanceof PDFDict) {
    for (const [key, value] of object.entries()) {
      if (value instanceof PDFRef) {
        const replacement = replacements.get(value.toString());
        if (replacement) object.set(key, replacement);
      } else {
        replaceReferences(value, replacements, visited);
      }
    }
  } else if (object instanceof PDFArray) {
    for (let index = 0; index < object.size(); index += 1) {
      const value = object.get(index);
      if (value instanceof PDFRef) {
        const replacement = replacements.get(value.toString());
        if (replacement) object.set(index, replacement);
      } else {
        replaceReferences(value, replacements, visited);
      }
    }
  }
}

/** Losslessly shares identical image streams after independently generated PDF chapters are merged. */
export async function deduplicateC1PdfImages(
  document: PDFDocument,
): Promise<{ removedImages: number; savedStreamBytes: number }> {
  await document.flush();
  const state: FingerprintState = { document, cache: new Map(), active: new Set() };
  const canonical = new Map<string, PDFRef>();
  const replacements = new Map<string, PDFRef>();
  const duplicates: PDFRef[] = [];
  let savedStreamBytes = 0;
  const objects = document.context.enumerateIndirectObjects();
  for (const [ref, object] of objects) {
    if (!(object instanceof PDFRawStream) || object.dict.get(PDFName.of("Subtype")) !== PDFName.of("Image")) continue;
    const key = await fingerprint(object, state);
    if (!key) continue;
    const existing = canonical.get(key);
    if (existing) {
      replacements.set(ref.toString(), existing);
      duplicates.push(ref);
      savedStreamBytes += object.getContentsSize();
    } else {
      canonical.set(key, ref);
    }
  }
  if (duplicates.length) {
    const visited = new Set<PDFObject>();
    for (const [, object] of objects) replaceReferences(object, replacements, visited);
    for (const ref of duplicates) document.context.delete(ref);
  }
  return { removedImages: duplicates.length, savedStreamBytes };
}
